import { useEffect, useRef, useState } from "react";
import "./landing.css";

const REGISTER_URL = "https://app.hireone.ai/register";

// Opens the Elementor popup injected by the WordPress plugin.
// Falls back to REGISTER_URL if the popup is not present (e.g. local dev).
function openPopup(e: React.MouseEvent) {
  e.preventDefault();
  const fn = (window as any).openHireOnePopup;
  if (typeof fn === "function") {
    fn(e.nativeEvent);
  } else {
    window.location.href = REGISTER_URL;
  }
}

interface LandingPageProps {}

const TickSvg = () => (
  <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
    <path
      d="M1.5 4l2 2L6.5 2"
      stroke="#FF6A00"
      strokeWidth="1.4"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const CheckSvg = () => (
  <svg width="9" height="9" viewBox="0 0 9 9" fill="none">
    <path
      d="M1.5 4.5l2 2L7.5 2.5"
      stroke="#FF6A00"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function LandingPage(_props: LandingPageProps) {
  const [annual, setAnnual] = useState(false);
  const [chipsVisible, setChipsVisible] = useState(false);
  const [aiThinkingVisible, setAiThinkingVisible] = useState(true);
  const [candidates, setCandidates] = useState<
    Array<{
      name: string;
      role: string;
      score: number;
      color: string;
      initials: string;
      visible: boolean;
      fillWidth: string;
    }>
  >([]);
  const [dn1, setDn1] = useState("0");
  const [dn2, setDn2] = useState("0");
  const [dn3, setDn3] = useState("0h");
  const [barsGrown, setBarsGrown] = useState(false);

  const [rRes, setRRes] = useState(100);
  const [rVid, setRVid] = useState(20);
  const [rChat, setRChat] = useState(50);
  const [rSms, setRSms] = useState(200);
  const [rWa, setRWa] = useState(100);
  const [rPh, setRPh] = useState(60);
  const [rVs, setRVs] = useState(20);
  const [rAs, setRAs] = useState(50);
  const [rDs, setRDs] = useState(200);

  const statsRef = useRef<HTMLDivElement>(null);
  const statsAnimated = useRef(false);
  const [s1, setS1] = useState(0);
  const [s2, setS2] = useState(0);
  const [s3, setS3] = useState(0);
  const [s4, setS4] = useState(0);

  const fmt = (n: number) => (n >= 1000 ? n.toLocaleString() : String(n));

  const cRes = rRes * 5,
    cVid = rVid * 20,
    cChat = rChat * 2;
  const cSms = rSms,
    cWa = rWa * 2,
    cPh = rPh * 5;
  const dataGB = +(rVs * 0.5 + rAs * 0.08 + rDs * 0.001).toFixed(1);
  const totAI = cRes + cVid + cChat + cSms + cWa + cPh;

  let recPlan = "Startup",
    recPrice = "Free";
  if (totAI > 10000 || dataGB > 200) {
    recPlan = "Enterprise";
    recPrice = "Custom pricing";
  } else if (totAI > 500 || dataGB > 5) {
    recPlan = "Team";
    recPrice = `$${annual ? 80 : 100}/user/mo`;
  }
  if (recPlan !== "Enterprise" && recPlan !== "Startup")
    recPrice += ` · billed ${annual ? "annually" : "monthly"}`;

  const teamPrice = annual ? 80 : 100;
  const teamNote = `$${teamPrice}/month · 1 user · billed ${annual ? "annually" : "monthly"} · up to 25 seats`;

  useEffect(() => {
    setTimeout(() => setChipsVisible(true), 200);

    setTimeout(() => setBarsGrown(true), 1800);

    const candidateData = [
      {
        name: "Sarah Robertson",
        role: "Ex-Google · React · 7 yrs",
        score: 96,
        color: "#FF6A00",
      },
      {
        name: "Marcus Kimura",
        role: "Stripe · TypeScript · 5 yrs",
        score: 91,
        color: "#3D445E",
      },
      {
        name: "Layla Patel",
        role: "Figma · Vue · Next.js · 4 yrs",
        score: 87,
        color: "#5C4D8A",
      },
    ];

    setTimeout(() => {
      setAiThinkingVisible(false);
      candidateData.forEach((c, i) => {
        setTimeout(() => {
          setCandidates((prev) => [
            ...prev,
            {
              ...c,
              initials: c.name
                .split(" ")
                .map((n) => n[0])
                .join(""),
              visible: false,
              fillWidth: "0%",
            },
          ]);
          setTimeout(() => {
            setCandidates((prev) =>
              prev.map((p, j) =>
                j === prev.length - 1 ? { ...p, visible: true } : p,
              ),
            );
            setTimeout(() => {
              setCandidates((prev) =>
                prev.map((p, j) =>
                  j === prev.length - 1
                    ? { ...p, fillWidth: `${c.score}%` }
                    : p,
                ),
              );
            }, 200);
          }, 50);
        }, i * 200);
      });
    }, 2200);

    function animNum(
      setter: (v: string) => void,
      target: number,
      suffix: string,
    ) {
      let v = 0;
      const step = target / 40;
      const t = setInterval(() => {
        v += step;
        if (v >= target) {
          setter(target + suffix);
          clearInterval(t);
        } else setter(Math.floor(v) + suffix);
      }, 40);
    }
    setTimeout(() => {
      animNum(setDn1, 247, "");
      animNum(setDn2, 18, "");
      setTimeout(() => setDn3("41h"), 1000);
    }, 1600);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1 },
    );
    document.querySelectorAll(".lp-reveal").forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    if (!statsRef.current || statsAnimated.current) return;
    const sobs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !statsAnimated.current) {
          statsAnimated.current = true;
          const counters: Array<{
            setter: (v: number) => void;
            target: number;
          }> = [
            { setter: setS1, target: 78 },
            { setter: setS2, target: 3 },
            { setter: setS3, target: 12 },
            { setter: setS4, target: 97 },
          ];
          counters.forEach(({ setter, target }) => {
            let v = 0;
            const step = target / 55;
            const timer = setInterval(() => {
              v += step;
              if (v >= target) {
                setter(target);
                clearInterval(timer);
              } else setter(Math.floor(v));
            }, 22);
          });
          sobs.disconnect();
        }
      },
      { threshold: 0.3 },
    );
    sobs.observe(statsRef.current);
    return () => sobs.disconnect();
  }, []);

  const heroWords = [
    { text: "Hire", accent: false },
    { text: "the", accent: false },
    { text: "Right", accent: true },
    { text: "Person", accent: true },
    { text: "BR", accent: false },
    { text: "10×", accent: false },
    { text: "Faster", accent: false },
    { text: "with", accent: false },
    { text: "AI", accent: false },
  ];

  const barHeights = ["28%", "44%", "60%", "76%", "100%", "66%", "37%", "18%"];

  return (
    <div
      style={{
        fontFamily: "'DM Sans', sans-serif",
        background: "#fff",
        color: "#1A1D2E",
        overflowX: "hidden",
        WebkitFontSmoothing: "antialiased",
      }}
    >
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link
        href="https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;1,400&display=swap"
        rel="stylesheet"
      />

      {/* HERO */}
      <section className="lp-hero">
        <div className="lp-hero-grid" />
        <div className="lp-orb lp-orb1" />
        <div className="lp-orb lp-orb2" />
        <div className="lp-orb lp-orb3" />

        {/* Floating chips */}
        <div
          className={`lp-float-chip lp-chip-stat lp-chip1${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-stat-ico"
            style={{ background: "rgba(255,106,0,.1)" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6" stroke="#FF6A00" strokeWidth="1.6" />
              <path
                d="M8 5v3l2 2"
                stroke="#FF6A00"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-stat-num" style={{ color: "#FF6A00" }}>
              78%
            </div>
            <div className="lp-chip-stat-lbl">
              Reduction in
              <br />
              time-to-shortlist
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-stat lp-chip2${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-stat-ico"
            style={{ background: "rgba(92,77,138,.1)" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <circle cx="8" cy="8" r="6" stroke="#5C4D8A" strokeWidth="1.6" />
              <path
                d="M5.5 8l2 2L11 6"
                stroke="#5C4D8A"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-stat-num" style={{ color: "#5C4D8A" }}>
              98%
            </div>
            <div className="lp-chip-stat-lbl">
              AI screening
              <br />
              accuracy
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-stat lp-chip3${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-stat-ico"
            style={{ background: "rgba(34,197,94,.1)" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M2 12l4-4 3 3 5-6"
                stroke="#22c55e"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-stat-num" style={{ color: "#22c55e" }}>
              3×
            </div>
            <div className="lp-chip-stat-lbl">
              More qualified hires
              <br />
              per quarter
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-stat lp-chip4${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-stat-ico"
            style={{ background: "rgba(255,106,0,.1)" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2v12M5 9.5c0 1.1 1.3 2 3 2s3-.9 3-2-1.3-2-3-2-3-.9-3-2 1.3-2 3-2 3 .9 3 2"
                stroke="#FF6A00"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-stat-num" style={{ color: "#FF6A00" }}>
              $18K
            </div>
            <div className="lp-chip-stat-lbl">
              Avg. savings
              <br />
              per month
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-notify lp-chip5${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-notify-ico"
            style={{ background: "rgba(61,68,94,.07)" }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle
                cx="7"
                cy="5"
                r="2.5"
                stroke="#3D445E"
                strokeWidth="1.4"
              />
              <path
                d="M2 12c0-2.8 2.2-5 5-5s5 2.2 5 5"
                stroke="#3D445E"
                strokeWidth="1.4"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-notify-lbl">
              <span className="lp-chip-live-dot" />
              New applicant scored
            </div>
            <div className="lp-chip-notify-val">
              Sarah R. — <span style={{ color: "#FF6A00" }}>96 pts</span>
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-stat lp-chip6${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-stat-ico"
            style={{ background: "rgba(61,68,94,.08)" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <rect
                x="2"
                y="4"
                width="12"
                height="9"
                rx="1.5"
                stroke="#3D445E"
                strokeWidth="1.5"
              />
              <path
                d="M5 4V3a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v1"
                stroke="#3D445E"
                strokeWidth="1.5"
              />
              <path
                d="M5 9h6"
                stroke="#3D445E"
                strokeWidth="1.3"
                strokeLinecap="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-stat-num" style={{ color: "#3D445E" }}>
              41h
            </div>
            <div className="lp-chip-stat-lbl">
              Saved per month
              <br />
              vs manual review
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-stat lp-chip7${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-stat-ico"
            style={{ background: "rgba(255,106,0,.1)" }}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M8 2l1.8 3.6L14 6.4l-3 2.9.7 4.1L8 11.4l-3.7 2 .7-4.1L2 6.4l4.2-.8z"
                stroke="#FF6A00"
                strokeWidth="1.4"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-stat-num" style={{ color: "#FF6A00" }}>
              97%
            </div>
            <div className="lp-chip-stat-lbl">
              Customer satisfaction
              <br />
              score
            </div>
          </div>
        </div>

        <div
          className={`lp-float-chip lp-chip-notify lp-chip8${chipsVisible ? " c-visible" : ""}`}
        >
          <div
            className="lp-chip-notify-ico"
            style={{ background: "rgba(34,197,94,.1)" }}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <circle
                cx="7"
                cy="7"
                r="5.5"
                stroke="#22c55e"
                strokeWidth="1.4"
              />
              <path
                d="M4.5 7l1.8 1.8L9.5 4.5"
                stroke="#22c55e"
                strokeWidth="1.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <div>
            <div className="lp-chip-notify-lbl">
              <span className="lp-chip-live-dot" />
              AI just ranked
            </div>
            <div className="lp-chip-notify-val">
              <span style={{ color: "#FF6A00" }}>247</span> applicants in 8s
            </div>
          </div>
        </div>

        {/* Hero content */}
        <div className="lp-hero-content">
          <div className="lp-hero-pill">
            <div className="lp-pdot" />
            Now with GPT-4 Vision Screening
          </div>

          <h1 className="lp-hero-h1">
            {heroWords.map((w, i) => {
              if (w.text === "BR") return <br key={i} />;
              const wordIndex = i > 4 ? i - 1 : i;
              const needsSpace = i > 0 && heroWords[i - 1]?.text !== "BR";
              return (
                <span key={i}>
                  {needsSpace && " "}
                  <span
                    className={`lp-word${w.accent ? " lp-accent" : ""}`}
                    style={{ animationDelay: `${0.15 + wordIndex * 0.1}s` }}
                  >
                    {w.text}
                  </span>
                </span>
              );
            })}
          </h1>

          <p className="lp-hero-sub">
            HireOne automatically screens, scores, and ranks candidates — so
            your team spends zero time on unqualified applications.
          </p>

          <div className="lp-hero-cta">
            <a className="lp-btn-hero" href="#" onClick={openPopup}>
              Start Screening Free
            </a>
            <a className="lp-btn-outline-hero" href={REGISTER_URL}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <circle
                  cx="7.5"
                  cy="7.5"
                  r="6.75"
                  stroke="#3D445E"
                  strokeWidth="1.5"
                />
                <polygon points="6,4.5 11,7.5 6,10.5" fill="#3D445E" />
              </svg>
              Watch Demo
            </a>
          </div>

          <div className="lp-hero-trust">
            <div className="lp-trust-avatars">
              <div
                className="lp-trust-av"
                style={{ background: "#FF6A00", marginLeft: 0 }}
              >
                JM
              </div>
              <div className="lp-trust-av" style={{ background: "#3D445E" }}>
                TL
              </div>
              <div className="lp-trust-av" style={{ background: "#5C4D8A" }}>
                AK
              </div>
              <div className="lp-trust-av" style={{ background: "#22c55e" }}>
                SR
              </div>
              <div className="lp-trust-av" style={{ background: "#e05e00" }}>
                MK
              </div>
            </div>
            <div className="lp-trust-text">
              <div className="lp-trust-stars">★★★★★</div>
              Trusted by <strong>3,000+</strong> talent teams worldwide
            </div>
          </div>

          {/* Dashboard mockup */}
          <div className="lp-hero-visual">
            <div className="lp-df">
              <div className="lp-dfbar">
                <div className="lp-dot r" />
                <div className="lp-dot y" />
                <div className="lp-dot g" />
                <span className="lp-barlabel">
                  HireOne Dashboard — Senior Frontend Engineer · 247 Applicants
                </span>
                <div className="lp-live-badge">
                  <div className="lp-live-dot" />
                  Live
                </div>
              </div>
              <div style={{ position: "relative", overflow: "hidden" }}>
                <div className="lp-scan-line" />
                <div className="lp-dbody">
                  <div className="lp-dc">
                    <div className="lp-dlabel">AI Screened</div>
                    <div className="lp-dnum">{dn1}</div>
                    <div className="lp-dsub">Candidates analyzed</div>
                    <div className="lp-dbadge">▲ 32% this week</div>
                  </div>
                  <div className="lp-dc">
                    <div className="lp-dlabel">Qualified</div>
                    <div className="lp-dnum">{dn2}</div>
                    <div className="lp-dsub">Match threshold 85%+</div>
                    <div className="lp-dbadge">Top talent flagged</div>
                  </div>
                  <div className="lp-dc">
                    <div className="lp-dlabel">Time Saved</div>
                    <div className="lp-dnum">{dn3}</div>
                    <div className="lp-dsub">vs manual review</div>
                    <div className="lp-dbadge">98% accuracy</div>
                  </div>

                  <div className="lp-dc wide">
                    <div className="lp-dlabel">Top Candidates — AI Ranked</div>
                    {aiThinkingVisible && (
                      <div className="lp-ai-thinking">
                        <div className="lp-ai-dot" />
                        <div className="lp-ai-dot" />
                        <div className="lp-ai-dot" />
                        <div className="lp-ai-label">
                          AI analyzing new application…
                        </div>
                      </div>
                    )}
                    <div className="lp-clist">
                      {candidates.map((c, i) => (
                        <div
                          key={i}
                          className="lp-citem"
                          style={{
                            opacity: c.visible ? 1 : 0,
                            transform: c.visible ? "none" : "translateX(-8px)",
                            transition: "all .4s ease",
                          }}
                        >
                          <div
                            className="lp-av"
                            style={{ background: c.color }}
                          >
                            {c.initials}
                          </div>
                          <div className="lp-ci">
                            <div className="lp-cname">{c.name}</div>
                            <div className="lp-crole">{c.role}</div>
                          </div>
                          <div className="lp-sw">
                            <div className="lp-str">
                              <div
                                className="lp-sf"
                                style={{ width: c.fillWidth }}
                              />
                            </div>
                            <div className="lp-sn">{c.score}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="lp-dc">
                    <div className="lp-dlabel">Score Distribution</div>
                    <div className="lp-bars">
                      {barHeights.map((h, i) => (
                        <div
                          key={i}
                          className={`lp-bar${i === 4 ? " hi" : ""}${barsGrown ? " grown" : ""}`}
                          style={{ height: h, transitionDelay: `${i * 80}ms` }}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LOGOS */}
      <div className="lp-logos">
        <div className="lp-llabel">Trusted by talent teams at</div>
        <div className="lp-lrow">
          {[
            "Salesforce",
            "Stripe",
            "Shopify",
            "Figma",
            "Notion",
            "Linear",
            "Vercel",
          ].map((c) => (
            <div key={c} className="lp-lco">
              {c}
            </div>
          ))}
        </div>
      </div>

      {/* STATS */}
      <div className="lp-stats" ref={statsRef}>
        <div className="lp-stat lp-reveal">
          <div className="lp-stn">
            <span>{s1}</span>
            <em>%</em>
          </div>
          <div className="lp-stl">Reduction in time-to-shortlist</div>
        </div>
        <div className="lp-stat lp-reveal">
          <div className="lp-stn">
            <span>{s2}</span>
            <em>×</em>
          </div>
          <div className="lp-stl">More qualified hires per quarter</div>
        </div>
        <div className="lp-stat lp-reveal">
          <div className="lp-stn">
            <span>{s3}</span>
            <em>M+</em>
          </div>
          <div className="lp-stl">Resumes screened to date</div>
        </div>
        <div className="lp-stat lp-reveal">
          <div className="lp-stn">
            <span>{s4}</span>
            <em>%</em>
          </div>
          <div className="lp-stl">Customer satisfaction score</div>
        </div>
      </div>

      {/* FEATURES */}
      <section className="lp-features" id="features">
        <div className="lp-sh lp-reveal">
          <div className="lp-ey">Platform Features</div>
          <h2>Everything you need to hire smarter</h2>
          <p>
            From first application to final offer — HireOne's AI handles the
            heavy lifting.
          </p>
        </div>
        <div className="lp-fgrid lp-reveal">
          <div className="lp-fc">
            <div className="lp-fico">
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                stroke="#FF6A00"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="11" cy="11" r="9" />
                <path d="M11 7v4l3 3" />
              </svg>
            </div>
            <h3>Instant AI Screening</h3>
            <p>
              Screen hundreds of applications in seconds. Our AI reads resumes,
              cover letters, and portfolio links to surface the best matches.
            </p>
            <span className="lp-ftag">GPT-4 Powered</span>
          </div>
          <div className="lp-fc">
            <div className="lp-fico">
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                stroke="#FF6A00"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 5h16M3 11h10M3 17h7" />
                <circle cx="18" cy="16" r="3" />
                <path d="M20 18l1.5 1.5" />
              </svg>
            </div>
            <h3>Smart Candidate Ranking</h3>
            <p>
              Every applicant receives a precision match score based on skills,
              experience, culture fit, and role requirements you define.
            </p>
            <span className="lp-ftag">Bias Reduced</span>
          </div>
          <div className="lp-fc">
            <div className="lp-fico">
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                stroke="#FF6A00"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="3" y="4" width="16" height="14" rx="2" />
                <path d="M8 2v4M14 2v4M3 10h16" />
              </svg>
            </div>
            <h3>Auto Scheduling</h3>
            <p>
              HireOne syncs with your calendar and auto-schedules interviews
              with top candidates — no back-and-forth emails ever again.
            </p>
            <span className="lp-ftag">Calendar Sync</span>
          </div>
          <div className="lp-fc">
            <div className="lp-fico">
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                stroke="#FF6A00"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
              </svg>
            </div>
            <h3>AI Video Interviews</h3>
            <p>
              Send async video screening prompts and let AI analyze responses —
              tone, content, and communication style — before your team reviews.
            </p>
            <span className="lp-ftag">Async First</span>
          </div>
          <div className="lp-fc">
            <div className="lp-fico">
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                stroke="#FF6A00"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              </svg>
            </div>
            <h3>Candidate Insights Chat</h3>
            <p>
              Ask your AI recruiter anything — instant, reasoned answers with
              evidence about every candidate in your pipeline.
            </p>
            <span className="lp-ftag">Conversational AI</span>
          </div>
          <div className="lp-fc">
            <div className="lp-fico">
              <svg
                width="20"
                height="20"
                viewBox="0 0 22 22"
                fill="none"
                stroke="#FF6A00"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
            </div>
            <h3>Hiring Analytics</h3>
            <p>
              Track pipeline velocity, source quality, drop-off rates, and
              diversity metrics in real time. Data-driven decisions, every time.
            </p>
            <span className="lp-ftag">Live Dashboard</span>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="lp-how" id="how">
        <div className="lp-sh lp-reveal">
          <div className="lp-ey">How It Works</div>
          <h2>From job post to shortlist in minutes</h2>
          <p>Four steps so simple your whole team will actually use it.</p>
        </div>
        <div className="lp-steps lp-reveal">
          <div className="lp-step">
            <div className="lp-stpn">01</div>
            <h4>Post Your Role</h4>
            <p>
              Define the role, skills, and culture preferences. HireOne builds a
              custom AI scoring model instantly.
            </p>
          </div>
          <div className="lp-step">
            <div className="lp-stpn">02</div>
            <h4>Collect Applications</h4>
            <p>
              Share your branded page. Candidates apply and HireOne analyzes
              every submission in real time.
            </p>
          </div>
          <div className="lp-step">
            <div className="lp-stpn">03</div>
            <h4>Review AI Shortlist</h4>
            <p>
              See ranked candidates with match scores, skill highlights, and AI
              commentary on each applicant.
            </p>
          </div>
          <div className="lp-step">
            <div className="lp-stpn">04</div>
            <h4>Interview &amp; Hire</h4>
            <p>
              One click to schedule. Interviews auto-recorded and summarized.
              Make offers with total confidence.
            </p>
          </div>
        </div>
      </section>

      {/* SPLIT - Fairness */}
      <section className="lp-split">
        <div className="lp-splitc lp-reveal">
          <div className="lp-ey">Fairness by Design</div>
          <h2>Eliminate bias before it costs you great talent</h2>
          <p>
            HireOne's AI evaluates skills and potential — not names,
            universities, or employment gaps.
          </p>
          <div className="lp-checks">
            <div className="lp-ck">
              <div className="lp-ckico">
                <CheckSvg />
              </div>
              Anonymized resume screening mode
            </div>
            <div className="lp-ck">
              <div className="lp-ckico">
                <CheckSvg />
              </div>
              Skills-first, credential-agnostic scoring
            </div>
            <div className="lp-ck">
              <div className="lp-ckico">
                <CheckSvg />
              </div>
              Real-time diversity pipeline tracking
            </div>
            <div className="lp-ck">
              <div className="lp-ckico">
                <CheckSvg />
              </div>
              EEOC compliance reporting built-in
            </div>
          </div>
        </div>
        <div className="lp-splitv lp-reveal">
          <div className="lp-splitvi">
            <div
              style={{
                fontSize: "10px",
                fontWeight: 700,
                letterSpacing: ".07em",
                textTransform: "uppercase" as const,
                color: "var(--lmuted)",
                marginBottom: "16px",
              }}
            >
              Diversity Pipeline
            </div>
            <div className="lp-pbrow">
              {[
                { label: "Gender Balance", pct: "52 / 48%", width: "52%" },
                { label: "Underrepresented Groups", pct: "38%", width: "38%" },
                {
                  label: "Non-Traditional Backgrounds",
                  pct: "61%",
                  width: "61%",
                },
                { label: "Career Changers Accepted", pct: "24%", width: "24%" },
              ].map((p, i) => (
                <div key={i} className="lp-pbi">
                  <div className="lp-pbt">
                    <span>{p.label}</span>
                    <span className="lp-pbpct">{p.pct}</span>
                  </div>
                  <div className="lp-pbtrack">
                    <div className="lp-pbfill" style={{ width: p.width }} />
                  </div>
                </div>
              ))}
            </div>
            <div
              style={{
                marginTop: "18px",
                padding: "12px",
                background: "var(--lo-light)",
                borderRadius: "9px",
                fontSize: "12px",
                color: "var(--lo)",
                fontWeight: 600,
              }}
            >
              ✓ EEOC Compliant &nbsp;·&nbsp; SOC 2 Type II &nbsp;·&nbsp; GDPR
              Ready
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="lp-testi" id="testi">
        <div className="lp-sh lp-reveal">
          <div className="lp-ey">What Customers Say</div>
          <h2>Built for teams that refuse to waste time</h2>
        </div>
        <div className="lp-tgrid lp-reveal">
          {[
            {
              text: '"We went from 3 weeks of screening to 2 days. HireOne is the first tool that actually delivers on the AI promise — not just buzzwords."',
              name: "Jamie Morales",
              role: "Head of Talent, Stripe",
              color: "#FF6A00",
              initials: "JM",
            },
            {
              text: '"The candidate chat feature alone is worth it. I can ask our AI recruiter anything and get evidence-backed answers in seconds."',
              name: "Tariq Lomax",
              role: "VP People, Vercel",
              color: "#3D445E",
              initials: "TL",
            },
            {
              text: '"We hired 12 engineers in 6 weeks using HireOne. Candidate quality through screening was genuinely better than our manual process."',
              name: "Anika Kovač",
              role: "CTO, Linear",
              color: "#5C4D8A",
              initials: "AK",
            },
          ].map((t, i) => (
            <div key={i} className="lp-tc">
              <div className="lp-stars">★★★★★</div>
              <div className="lp-ttext">{t.text}</div>
              <div className="lp-tauthor">
                <div className="lp-tav" style={{ background: t.color }}>
                  {t.initials}
                </div>
                <div>
                  <div className="lp-tname">{t.name}</div>
                  <div className="lp-trole">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="lp-pricing" id="pricing">
        <div className="lp-sh lp-reveal">
          <div className="lp-ey">Credit-Based Pricing</div>
          <h2>The right fit for every team.</h2>
          <p>
            All plans include full HireOne AI, 3 credit pools, and the complete
            Screen → Rank → Schedule → Hire pipeline.
          </p>
        </div>

        <div className="lp-billing-row lp-reveal">
          <span className={`lp-toggle-lbl${!annual ? " on" : ""}`}>
            Monthly
          </span>
          <button
            className={`lp-pill-toggle${annual ? " ann" : ""}`}
            onClick={() => setAnnual(!annual)}
          />
          <span className={`lp-toggle-lbl${annual ? " on" : ""}`}>Annual</span>
          <span className="lp-save-pill">Save 20%</span>
        </div>

        <div className="lp-cr-legend lp-reveal">
          <div className="lp-crl-item">
            <div className="lp-crl-dot" style={{ background: "#FF6A00" }} />
            Credits — AI screening, interviews, calls, SMS, WhatsApp & more
          </div>
          <div className="lp-crl-item">
            <div className="lp-crl-dot" style={{ background: "#5C4D8A" }} />
            Data Credits — AWS S3 storage at $0.060/GB (42%+ margin)
          </div>
        </div>

        <div className="lp-pgrid lp-reveal">
          {/* STARTUP */}
          <div className="lp-pc">
            <div className="lp-plan-badge lp-badge-free">FREE FOREVER</div>
            <div className="lp-p-eyebrow">Startup</div>
            <div className="lp-p-name">Startup</div>
            <div className="lp-p-desc">
              For founders &amp; solo operators testing HireOne AI before
              committing.
            </div>
            <div className="lp-p-price-row">
              <div className="lp-p-amt-free">Free</div>
            </div>
            <div className="lp-p-note">Up to 2 users — always free</div>
            <div className="lp-p-divider" />
            <div className="lp-credit-pools">
              <CreditPool
                color="#FF6A00"
                name="Credits"
                amount="1,000"
                unit="/ month"
                rates={[
                  ["Resume screen", "5 cr"],
                  ["Video analysis", "20 cr"],
                  ["AI chat query", "2 cr"],
                  ["SMS", "1 cr"],
                  ["WhatsApp", "2 cr"],
                  ["Phone call", "5 cr/min"],
                ]}
              />
              <CreditPool
                color="#5C4D8A"
                name="Data Credits"
                amount="5 GB"
                unit="AWS S3"
                rates={[
                  ["Video interview", "~500 MB"],
                  ["Audio recording", "~80 MB"],
                  ["Transcripts", "~1 MB"],
                ]}
              />
            </div>
            <div className="lp-p-features">
              {[
                "Up to 2 users",
                "HireOne AI — all 4 channels",
                "1 active job post",
                "Screen → Rank → Schedule pipeline",
                "Buy credit top-up packs anytime",
              ].map((f, i) => (
                <div key={i} className="lp-pf">
                  <div className="lp-pf-tick">
                    <TickSvg />
                  </div>
                  {f}
                </div>
              ))}
            </div>
            <a className="lp-btn-plan" href="#" onClick={openPopup}>
              Get Started Free
            </a>
          </div>

          {/* TEAM */}
          <div className="lp-pc lp-feat">
            <div className="lp-plan-badge lp-badge-popular">MOST POPULAR</div>
            <div className="lp-p-eyebrow">Team</div>
            <div className="lp-p-name">Team</div>
            <div className="lp-p-desc">
              For talent teams ready to deploy HireOne at full scale and fill
              roles fast.
            </div>
            <div className="lp-p-price-row">
              <div className="lp-p-cur">$</div>
              <div className="lp-p-amt">{teamPrice}</div>
              <div className="lp-p-per">/user/mo</div>
            </div>
            <div className="lp-p-note">{teamNote}</div>
            <div className="lp-p-divider" />
            <div className="lp-credit-pools">
              <CreditPool
                feat
                color="#FF6A00"
                name="Credits"
                amount="7,500"
                unit="/ month"
                rates={[
                  ["Resume screen", "5 cr"],
                  ["Video analysis", "20 cr"],
                  ["AI chat query", "2 cr"],
                  ["SMS", "1 cr"],
                  ["WhatsApp", "2 cr"],
                  ["Phone call", "5 cr/min"],
                ]}
              />
              <CreditPool
                feat
                color="#5C4D8A"
                name="Data Credits"
                amount="50 GB"
                unit="AWS S3"
                rates={[
                  ["Video interview", "~500 MB"],
                  ["Audio recording", "~80 MB"],
                  ["Transcripts", "~1 MB"],
                ]}
              />
            </div>
            <div className="lp-p-features">
              {[
                "Everything in Startup",
                "3–25 users · $100/user/mo",
                "Unlimited job posts & workspaces",
                "Team credit budgets & allocation",
                "AI video interviews + async scoring",
                "Priority AI queue · 2× faster screening",
                "Advanced hiring analytics",
                "Email + live chat support",
              ].map((f, i) => (
                <div key={i} className="lp-pf">
                  <div className="lp-pf-tick">
                    <TickSvg />
                  </div>
                  {f}
                </div>
              ))}
            </div>
            <a className="lp-btn-plan lp-cta-go" href="#" onClick={openPopup}>
              Start 14-Day Free Trial
            </a>
          </div>

          {/* ENTERPRISE */}
          <div className="lp-pc">
            <div className="lp-p-eyebrow">Enterprise</div>
            <div className="lp-p-name">Enterprise</div>
            <div className="lp-p-desc">
              For large organizations with complex hiring, compliance, and
              integration needs.
            </div>
            <div className="lp-p-price-row">
              <div className="lp-p-amt-free">Custom</div>
            </div>
            <div className="lp-p-note">
              Tailored to your team size &amp; hiring volume
            </div>
            <div className="lp-p-divider" />
            <div className="lp-credit-pools">
              <CreditPool
                color="#FF6A00"
                name="Credits"
                amount="Custom"
                rates={[
                  ["Dedicated AI capacity", "incl."],
                  ["Custom model fine-tuning", "incl."],
                  ["Dedicated phone lines", "incl."],
                  ["White-label SMS sender", "incl."],
                ]}
              />
              <CreditPool
                color="#5C4D8A"
                name="Data Credits"
                amount="Custom"
                rates={[
                  ["Private AWS region + S3", "incl."],
                  ["7-year retention", "incl."],
                ]}
              />
            </div>
            <div className="lp-p-features">
              {[
                "Everything in Team",
                "25+ users · unlimited seats",
                "CRM integrations (Salesforce, HubSpot)",
                "Custom API + webhook integrations",
                "SOC 2 Type II · 99.9% SLA",
                "Dedicated support manager",
                "Custom onboarding & team training",
              ].map((f, i) => (
                <div key={i} className="lp-pf">
                  <div className="lp-pf-tick">
                    <TickSvg />
                  </div>
                  {f}
                </div>
              ))}
            </div>
            <a className="lp-btn-plan" href="#" onClick={openPopup}>
              Contact Sales →
            </a>
          </div>
        </div>

        <div className="lp-topup-note">
          Credits roll over 3 months &nbsp;·&nbsp; Top-up any pool anytime
          &nbsp;·&nbsp; <a href="#">View full credit rate card →</a>
        </div>

        {/* RATE PANEL */}
        <div className="lp-rate-panel lp-reveal">
          <div className="lp-rate-panel-head">
            <div
              style={{
                fontSize: "11px",
                fontWeight: 700,
                letterSpacing: ".1em",
                textTransform: "uppercase" as const,
                color: "var(--lo)",
                marginBottom: "6px",
              }}
            >
              Pricing Transparency
            </div>
            <div
              style={{
                fontFamily: "'Plus Jakarta Sans',sans-serif",
                fontSize: "17px",
                fontWeight: 700,
                color: "var(--ltext)",
                letterSpacing: "-.02em",
              }}
            >
              How we calculate credit costs
            </div>
            <div
              style={{
                fontSize: "12px",
                color: "var(--lmuted)",
                marginTop: "3px",
              }}
            >
              Based on Amazon Connect + Twilio market rates · AWS S3 Standard
              storage · 30% markup on highest provider · Updated March 2025
            </div>
          </div>
          <div className="lp-rate-grid">
            <RateCard
              color="#3D445E"
              title="SMS — per message"
              rows={[
                {
                  vendor: "Amazon Connect",
                  cost: "$0.0100",
                  high: true,
                  tag: true,
                },
                { vendor: "Twilio", cost: "$0.0083" },
              ]}
              calc="$0.0100 × 1.30"
              result="= $0.013 / SMS"
              credit="1 credit = 1 SMS message"
            />
            <RateCard
              color="#22c55e"
              title="WhatsApp — per message"
              rows={[
                { vendor: "Twilio", cost: "$0.0050", high: true, tag: true },
                { vendor: "Amazon Connect", cost: "$0.0040" },
              ]}
              calc="$0.0050 × 1.30"
              result="= $0.0065 / msg"
              credit="2 credits = 1 WhatsApp message"
            />
            <RateCard
              color="#FF6A00"
              title="Phone call — per minute"
              rows={[
                {
                  vendor: "Amazon Connect",
                  cost: "$0.0380",
                  high: true,
                  tag: true,
                },
                { vendor: "Twilio outbound", cost: "$0.0140" },
              ]}
              calc="$0.0380 × 1.30"
              result="= $0.049 / min"
              credit="5 credits = 1 minute of calling"
            />
            <RateCard
              color="#5C4D8A"
              title="Storage — per GB / month"
              rows={[
                { vendor: "S3 Standard storage", cost: "$0.0230" },
                { vendor: "Requests + egress", cost: "~$0.0120" },
                {
                  vendor: "Total AWS cost",
                  cost: "$0.035 / GB",
                  high: true,
                  border: true,
                },
              ]}
              calc="$0.035 ÷ 0.58 (42% margin)"
              result="= $0.060 / GB"
              credit="1 GB data = $0.060 in data credits"
            />
          </div>
          <div
            style={{
              padding: "12px 28px",
              fontSize: "11px",
              color: "var(--lmuted)",
              borderTop: "1px solid var(--lborder)",
            }}
          >
            Rates based on US-East-1 region · International call rates vary by
            destination · Markup covers platform infrastructure, compliance,
            support &amp; overhead · Storage margin &gt;40% as required for AWS
            overhead recovery
          </div>
        </div>
      </section>

      {/* CALCULATOR */}
      <div className="lp-calcsec">
        <div className="lp-sh lp-reveal" style={{ marginBottom: "32px" }}>
          <div className="lp-ey">Credit Calculator</div>
          <h2>Estimate your monthly usage</h2>
          <p>
            Adjust sliders to match your hiring volume — we'll recommend the
            right plan.
          </p>
        </div>
        <div className="lp-calcbox lp-reveal">
          <div className="lp-calchead">
            <div>
              <h3>Monthly Credit Estimator</h3>
              <p>Adjust usage across all three credit pools</p>
            </div>
            <div
              style={{
                fontSize: "11px",
                color: "var(--lmuted)",
                textAlign: "right" as const,
              }}
            >
              Credits reset monthly
              <br />
              Unused credits roll over 3 months
            </div>
          </div>
          <div className="lp-calcbody">
            <div className="lp-calcol">
              <h4>
                <div className="lp-calcdot" style={{ background: "#FF6A00" }} />
                <span style={{ color: "#FF6A00" }}>Credits</span>
              </h4>
              <CalcRow
                label="Resumes screened / month"
                value={rRes}
                max={500}
                onChange={setRRes}
                display={`${rRes} × 5 = `}
                result={fmt(cRes)}
                resultColor="var(--lo)"
                suffix=" cr"
              />
              <CalcRow
                label="Video interviews analyzed"
                value={rVid}
                max={100}
                onChange={setRVid}
                display={`${rVid} × 20 = `}
                result={fmt(cVid)}
                resultColor="var(--lo)"
                suffix=" cr"
              />
              <CalcRow
                label="AI chat queries"
                value={rChat}
                max={500}
                onChange={setRChat}
                display={`${rChat} × 2 = `}
                result={fmt(cChat)}
                resultColor="var(--lo)"
                suffix=" cr"
              />
              <CalcRow
                label="SMS messages sent"
                value={rSms}
                max={1000}
                onChange={setRSms}
                display={`${rSms} × 1 = `}
                result={fmt(cSms)}
                resultColor="var(--lo)"
                suffix=" cr"
              />
              <CalcRow
                label="WhatsApp messages"
                value={rWa}
                max={500}
                onChange={setRWa}
                display={`${rWa} × 2 = `}
                result={fmt(cWa)}
                resultColor="var(--lo)"
                suffix=" cr"
              />
              <CalcRow
                label="Phone call minutes"
                value={rPh}
                max={300}
                onChange={setRPh}
                display={`${rPh} × 5 = `}
                result={fmt(cPh)}
                resultColor="var(--lo)"
                suffix=" cr"
              />
              <div className="lp-calc-subtotal">
                <div className="lp-calc-subtotal-lbl">Total Credits</div>
                <div
                  className="lp-calc-subtotal-n"
                  style={{ color: "var(--lo)" }}
                >
                  {fmt(totAI)}
                </div>
              </div>
            </div>
            <div className="lp-calcol">
              <h4>
                <div className="lp-calcdot" style={{ background: "#5C4D8A" }} />
                <span style={{ color: "#5C4D8A" }}>Data Credits (AWS)</span>
              </h4>
              <CalcRow
                label="Video interviews stored"
                value={rVs}
                max={100}
                onChange={setRVs}
                display={`${rVs} ≈ `}
                result={`${(rVs * 0.5).toFixed(1)} GB`}
                resultColor="#5C4D8A"
              />
              <CalcRow
                label="Audio recordings stored"
                value={rAs}
                max={200}
                onChange={setRAs}
                display={`${rAs} ≈ `}
                result={`${(rAs * 0.08).toFixed(1)} GB`}
                resultColor="#5C4D8A"
              />
              <CalcRow
                label="Transcripts & docs"
                value={rDs}
                max={1000}
                onChange={setRDs}
                display={`${rDs} ≈ `}
                result={`${(rDs * 0.001).toFixed(1)} GB`}
                resultColor="#5C4D8A"
              />
              <div className="lp-calc-subtotal">
                <div className="lp-calc-subtotal-lbl">Total Storage</div>
                <div
                  className="lp-calc-subtotal-n"
                  style={{ color: "#5C4D8A" }}
                >
                  {dataGB} GB
                </div>
              </div>
            </div>
          </div>
          <div className="lp-calcresult">
            <div>
              <div
                style={{
                  fontSize: "10px",
                  color: "rgba(255,255,255,.38)",
                  fontWeight: 600,
                  letterSpacing: ".08em",
                  textTransform: "uppercase" as const,
                  marginBottom: "7px",
                }}
              >
                Recommended Plan
              </div>
              <div className="lp-rec-pill">
                <div
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "var(--lo)",
                  }}
                />
                <div>
                  <div className="lp-rec-name">{recPlan}</div>
                  <div className="lp-rec-sub">{recPrice}</div>
                </div>
              </div>
            </div>
            <div className="lp-rec-totals">
              <div className="lp-rec-total">
                <div className="lp-rec-total-n" style={{ color: "#FF6A00" }}>
                  {fmt(totAI)}
                </div>
                <div className="lp-rec-total-l">Credits</div>
              </div>
              <div className="lp-rec-total">
                <div className="lp-rec-total-n" style={{ color: "#9B8FCC" }}>
                  {dataGB} GB
                </div>
                <div className="lp-rec-total-l">Data Storage</div>
              </div>
            </div>
            <a className="lp-btn-rec" href="#" onClick={openPopup}>
              Get This Plan →
            </a>
          </div>
        </div>
      </div>

      {/* CTA */}
      <section className="lp-ctasec">
        <div className="lp-ctabox lp-reveal">
          <h2>
            Your next best hire is already
            <br />
            in your inbox. Find them in minutes.
          </h2>
          <p>Join 3,000+ companies using HireOne to build world-class teams.</p>
          <div className="lp-ctabtns">
            <a className="lp-btn-co" href="#" onClick={openPopup}>
              Schedule a Demo
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function CreditPool({
  color,
  name,
  amount,
  unit,
  rates,
  feat,
}: {
  color: string;
  name: string;
  amount: string;
  unit?: string;
  rates: string[][];
  feat?: boolean;
}) {
  return (
    <div className="lp-cr-pool">
      <div className="lp-cr-pool-head">
        <div className="lp-cr-pool-dot" style={{ background: color }} />
        <div className="lp-cr-pool-name">{name}</div>
      </div>
      <div className="lp-cr-pool-row">
        <div className="lp-cr-pool-amount">{amount}</div>
        {unit && <div className="lp-cr-pool-unit">{unit}</div>}
      </div>
      <div className="lp-cr-pool-rates">
        {rates.map((r, i) => (
          <div key={i} className="lp-cr-pool-rate">
            <span>{r[0]}</span>
            <span>{r[1]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RateCard({
  color,
  title,
  rows,
  calc,
  result,
  credit,
}: {
  color: string;
  title: string;
  rows: Array<{
    vendor: string;
    cost: string;
    high?: boolean;
    tag?: boolean;
    border?: boolean;
  }>;
  calc: string;
  result: string;
  credit: string;
}) {
  return (
    <div className="lp-rate-card">
      <div className="lp-rate-card-head">
        <div className="lp-rate-dot" style={{ background: color }} />
        <div className="lp-rate-title">{title}</div>
      </div>
      <div className="lp-rate-compare">
        {rows.map((r, i) => (
          <div
            key={i}
            className="lp-rate-row"
            style={
              r.border
                ? {
                    borderTop: "1px solid var(--lborder)",
                    paddingTop: "5px",
                    marginTop: "3px",
                  }
                : undefined
            }
          >
            <span
              className="lp-rate-vendor"
              style={
                r.border
                  ? { fontWeight: 600, color: "var(--ltext)" }
                  : undefined
              }
            >
              {r.vendor}
            </span>
            <span className={`lp-rate-cost${r.high ? " high" : ""}`}>
              {r.cost}
            </span>
            {r.tag && <span className="lp-rate-tag">highest</span>}
          </div>
        ))}
      </div>
      <div className="lp-rate-calc">
        <span>{calc}</span>
        <span className="lp-rate-final">
          = <strong>{result.replace("= ", "")}</strong>
        </span>
      </div>
      <div className="lp-rate-credit">{credit}</div>
    </div>
  );
}

function CalcRow({
  label,
  value,
  max,
  onChange,
  display,
  result,
  resultColor,
  suffix,
}: {
  label: string;
  value: number;
  max: number;
  onChange: (v: number) => void;
  display: string;
  result: string;
  resultColor: string;
  suffix?: string;
}) {
  return (
    <div className="lp-calcrow">
      <label>{label}</label>
      <input
        type="range"
        min={0}
        max={max}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value))}
      />
      <div className="lp-cv">
        {display}
        <span style={{ color: resultColor }}>{result}</span>
        {suffix || ""}
      </div>
    </div>
  );
}
